import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { DocumentDto, DocumentTypeDto } from '@lawyer/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useDocumentTypes } from '../../hooks/useLookups'
import { api } from '../../lib/api'
import { formatBytes } from '../../lib/format'
import { qk } from '../../lib/queryClient'
import { ErrorAlert } from '../common/Feedback'
import { FileDropZone } from './FileDropZone'

export type UploadScope = {
  caseId?: string
  clientId?: string
  taskId?: string
}

type Props = {
  open: boolean
  scope: UploadScope
  /** Optional client picker, used on the case documents panel. */
  clientChoices?: { id: string; label: string }[]
  /** Whose document this is, added to its name — the client card passes the client. */
  ownerName?: string
  /** A file already chosen outside the dialog, e.g. dropped on the panel. */
  defaultFile?: File | null
  onClose: () => void
  onUploaded: () => void
}

type TypeOption = DocumentTypeDto & { isNew?: boolean }

const filterTypes = createFilterOptions<TypeOption>()

const splitExtension = (fileName: string) => {
  const ext = /\.[^.]+$/.exec(fileName)?.[0] ?? ''
  return { base: fileName.slice(0, fileName.length - ext.length), ext }
}

/**
 * "דרכון - ישראל ישראלי.pdf": the type (or the file's own name) plus whose document it
 * is, so downloads of different clients tell apart. The extension too, or the download
 * has none.
 */
function suggestedName(type: DocumentTypeDto | null, file: File | null, owner: string) {
  const { base, ext } = splitExtension(file?.name ?? '')
  const label = type?.name ?? base
  if (!label) return ''
  return `${owner && !label.includes(owner) ? `${label} - ${owner}` : label}${ext}`
}

export function UploadDocumentDialog({
  open,
  scope,
  clientChoices,
  ownerName = '',
  defaultFile = null,
  onClose,
  onUploaded,
}: Props) {
  const queryClient = useQueryClient()
  const documentTypes = useDocumentTypes()
  const fileInput = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [type, setType] = useState<DocumentTypeDto | null>(null)
  const [name, setName] = useState('')
  /** Once the name is typed by hand, picking a type or a client no longer rewrites it. */
  const [nameEdited, setNameEdited] = useState(false)
  const [description, setDescription] = useState('')
  const [clientId, setClientId] = useState('')

  useEffect(() => {
    if (!open) return
    setFile(defaultFile)
    setType(null)
    setNameEdited(false)
    setDescription('')
    setClientId(scope.clientId ?? '')
    if (fileInput.current) fileInput.current.value = ''
  }, [open, scope.clientId, defaultFile])

  const owner =
    clientChoices?.find((choice) => choice.id === clientId)?.label ?? (clientChoices ? '' : ownerName)

  useEffect(() => {
    if (open && !nameEdited) setName(suggestedName(type, file, owner))
  }, [open, nameEdited, type, file, owner])

  const addType = useMutation({
    mutationFn: (typeName: string) =>
      api.post<DocumentTypeDto>('/document-types', { name: typeName }),
    onSuccess: (created) => {
      setType(created)
      void queryClient.invalidateQueries({ queryKey: qk.documentTypes })
    },
  })

  const upload = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('לא נבחר קובץ')
      const formData = new FormData()
      formData.append('file', file)
      if (name.trim()) formData.append('name', name.trim())
      if (description.trim()) formData.append('description', description.trim())
      if (type) formData.append('documentTypeId', type.id)
      if (scope.caseId) formData.append('caseId', scope.caseId)
      if (scope.taskId) formData.append('taskId', scope.taskId)
      if (clientId) formData.append('clientId', clientId)
      return api.upload<DocumentDto>('/documents', formData)
    },
    onSuccess: () => {
      onUploaded()
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>העלאת מסמך</DialogTitle>

      <DialogContent dividers>
        {upload.error ? <ErrorAlert error={upload.error} /> : null}
        {addType.error ? <ErrorAlert error={addType.error} /> : null}

        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <FileDropZone onFile={setFile} overlayText="שחררו את הקובץ כאן">
            <Button
              component="label"
              variant="outlined"
              fullWidth
              sx={{ py: 3, borderStyle: 'dashed', flexDirection: 'column' }}
            >
              {file ? 'בחירת קובץ אחר' : 'בחירת קובץ'}
              <Typography variant="caption" color="text.secondary">
                או גררו קובץ לכאן
              </Typography>
              <input
                ref={fileInput}
                type="file"
                hidden
                onChange={(e) => {
                  const selected = e.target.files?.[0]
                  if (selected) setFile(selected)
                }}
              />
            </Button>
          </FileDropZone>

          {file ? (
            <Typography variant="body2" color="text.secondary">
              {file.name} · {formatBytes(file.size)}
            </Typography>
          ) : null}

          <Autocomplete<TypeOption>
            options={documentTypes.data ?? []}
            value={type}
            loading={documentTypes.isPending || addType.isPending}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(all, state) => {
              const filtered = filterTypes(all, state)
              const typed = state.inputValue.trim()
              if (typed && !all.some((option) => option.name === typed)) {
                filtered.push({ id: '', name: typed, isNew: true })
              }
              return filtered
            }}
            renderOption={(props, option) => {
              const { key, ...rest } = props
              return (
                <li key={key} {...rest}>
                  {option.isNew ? `הוספת הסוג "${option.name}"` : option.name}
                </li>
              )
            }}
            onChange={(_event, option) => {
              if (option?.isNew) addType.mutate(option.name)
              else setType(option)
            }}
            noOptionsText="אין סוגים — הקלידו כדי להוסיף"
            renderInput={(params) => (
              <TextField
                {...params}
                label="סוג מסמך"
                placeholder="תעודת זהות, דרכון, נסח טאבו…"
                helperText="אפשר להקליד סוג חדש ולהוסיף אותו לרשימה"
              />
            )}
          />

          {clientChoices ? (
            <TextField
              select
              label="שיוך ללקוח בתיק"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              helperText="ריק = מסמך של התיק כולו"
            >
              <MenuItem value="">מסמך של התיק</MenuItem>
              {clientChoices.map((choice) => (
                <MenuItem key={choice.id} value={choice.id}>
                  {choice.label}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          <TextField
            label="שם תצוגה"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setNameEdited(e.target.value !== '')
            }}
            helperText={nameEdited ? undefined : 'נקבע לפי סוג המסמך ושם הלקוח — אפשר לשנות'}
          />
          <TextField
            label="תיאור"
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>ביטול</Button>
        <Button
          variant="contained"
          onClick={() => upload.mutate()}
          disabled={!file || upload.isPending || addType.isPending}
        >
          {upload.isPending ? 'מעלה…' : 'העלאה'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
